import { OpenAI } from 'openai';

import {
  DETECT_IMAGE_REPORTS,
  GENERIC_ERROR_MESSAGE,
  NULL_POSSIBLE_REPORT_PARAMS,
  PROXY_MODEL,
  RATE_LIMIT_EXCEEDED_ERROR_MESSAGE,
  RESPONSE_TYPE,
  SYSTEM_PROMPT,
} from 'components/ChatBV/constants';
import {
  ImageAnalysisRequest,
  InputItem,
  PossibleReportParams,
  ReponsesAPIRequestProps,
  ResponsesAPIResponse,
} from 'components/ChatBV/types';

const getRequest = (props: ReponsesAPIRequestProps, model: string) => {
  // Ensure input is always an array to prevent "is not iterable" errors
  const inputArray: InputItem[] = Array.isArray(props.input) ? props.input : [];
  return {
    model,
    input: [
      {
        role: 'system',
        content: [
          {
            type: 'input_text',
            text: SYSTEM_PROMPT,
          },
        ],
      },
      ...inputArray,
    ],
    tools: [
      {
        type: 'function',
        name: 'trigger_search',
        description:
          'Triggers a search based on phone, email, vehicle, person, or property information.',
        parameters: {
          type: 'object',
          properties: {
            search_type: {
              type: 'string',
              description:
                'The type of search to trigger. Must be one of: phone, email, vehicle, person, property',
              enum: [
                'phone',
                'email',
                'vehicle',
                'person',
                'property',
                'not_supported',
              ],
            },
            phone_number: {
              type: 'string',
              description:
                'The phone number to search for, should be a valid US phone number',
            },
            email: {
              type: 'string',
              description:
                'The email to search for, should have a @ and a ., the domain should be a valid email domain',
            },
            address: {
              type: 'string',
              description: 'The address to search for',
            },
            city: {
              type: 'string',
              description: 'The city to search for',
            },
            state: {
              type: 'string',
              description: 'The state to search for',
            },
            zipcode: {
              type: 'string',
              description: 'The zip code to search for',
            },
            vehicle_license_plate: {
              type: 'string',
              description: 'The license plate to search for',
            },
            vehicle_year: {
              type: 'string',
              description: 'The year to search for',
            },
            vehicle_make: {
              type: 'string',
              description: 'The make of the vehicle to search for',
            },
            vehicle_model: {
              type: 'string',
              description: 'The model of the vehicle to search for',
            },
            vehicle_trim: {
              type: 'string',
              description: 'The trim of the vehicle to search for',
            },
            vehicle_vin: {
              type: 'string',
              description: 'The VIN of the vehicle to search for',
            },
            first_name: {
              type: 'string',
              description: 'The first name of the person to search for',
            },
            last_name: {
              type: 'string',
              description: 'The last name of the person to search for',
            },
            middle_name: {
              type: 'string',
              description: 'The middle name of the person to search for',
            },
            age: {
              type: 'string',
              description: 'The age of the person to search for',
            },
            title: {
              type: 'string',
              description: 'The title of the person to search for',
            },
            company: {
              type: 'string',
              description: 'The company of the person to search for',
            },
            industry: {
              type: 'string',
              description: 'The industry of the person to search for',
            },
            level: {
              type: 'string',
              description: 'The level of the person to search for',
            },
            birth_month: {
              type: 'string',
              description: 'The birth month of the person to search for',
            },
            birth_year: {
              type: 'string',
              description: 'The birth year of the person to search for',
            },
            death_month: {
              type: 'string',
              description: 'The death month of the person to search for',
            },
            death_year: {
              type: 'string',
              description: 'The death year of the person to search for',
            },
          },
          required: ['search_type'],
          additionalProperties: false,
        },
        strict: false,
      },
      {
        type: 'web_search',
      },
      {
        type: 'function',
        name: 'get_specific_report_section',
        description:
          'Retrieves a specific section from a report based on the users query. You MUST set exactly ONE of the available boolean report section parameters to true that corresponds to the type of information the user is requesting. Each boolean parameter represents a different category of report data. If the user request doesnt exactly match a specific section, map it to the closest matching category.',
        parameters: {
          type: 'object',
          properties: {
            identity_or_owner_identity_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve identity or owner identity information including names, aliases, ages, and dates of birth.',
            },
            family_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve family info including family members, spouses, and children.',
            },
            email_phone_or_social_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve email, phone, or social info including phone numbers, emails, and social media profiles.',
            },
            trust_and_safety_status_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve trust and safety status info including fraud scan info.',
            },
            criminal_or_legal_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve criminal or legal info including criminal records and legal information.',
            },
            property_or_address_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve property or address info including property details, addresses, and possible residents.',
            },
            vehicle_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve vehicle info including vehicle details, vehicle history, and vehicle ownership.',
            },
            jobs_education_or_skills_info: {
              type: 'boolean',
              description:
                'Set to true to retrieve jobs, education, or skills info including jobs, education, and skills.',
            },
            permalink: {
              type: 'string',
              description:
                'The permalink of the report to get the specific report section from',
            },
          },
          required: [
            'identity_or_owner_identity_info',
            'family_info',
            'email_phone_or_social_info',
            'trust_and_safety_status_info',
            'criminal_or_legal_info',
            'property_or_address_info',
            'vehicle_info',
            'jobs_education_or_skills_info',
            'permalink',
          ],
          additionalProperties: false,
        },
        strict: true,
      },
      {
        type: 'function',
        name: 'customer_service_contact',
        description:
          'Provides the customer service contact information for the user.',
        strict: false,
      },
    ],
    ...(props?.previous_response_id && {
      previous_response_id: props.previous_response_id,
    }),
    truncation: 'auto',
  };
};

export const sendMessageToResponsesAPI = async (
  props: ReponsesAPIRequestProps,
  // eslint-disable-next-line no-unused-vars
  logError: (message: string, error: Error) => void
): Promise<ResponsesAPIResponse> => {
  // For local testing insert your API key here
  const openai = new OpenAI({
    apiKey: '',
    dangerouslyAllowBrowser: true,
  });
  const request = getRequest(props, 'gpt-4o-mini');
  const response = await openai.responses.create(request as any);

  try {
    // OpenAI responses don't have a status property like fetch responses
    // The response is already the parsed data if successful
    return {
      type: RESPONSE_TYPE.RESPONSE,
      data: response,
    };
  } catch (error) {
    logError('Error sending message to Responses API', error as Error);
    throw error;
  }
};

export const sendMessageToProxy = async (
  props: ReponsesAPIRequestProps,
  // eslint-disable-next-line no-unused-vars
  logError: (message: string, error: Error) => void
): Promise<ResponsesAPIResponse> => {
  const request = getRequest(props, PROXY_MODEL);
  try {
    const response = await fetch(
      `https://www.beenverified.com/ms/insight/direct/smartsearchbot/v1/responses?api-version=preview`,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );
    if (!response.ok) {
      return {
        type: RESPONSE_TYPE.ERROR,
        error: {
          message:
            response.status === 429
              ? RATE_LIMIT_EXCEEDED_ERROR_MESSAGE
              : GENERIC_ERROR_MESSAGE,
          type: RESPONSE_TYPE.ERROR,
          http_code: response.status,
        },
      };
    }
    const data = (await response.json()) as OpenAI.Responses.Response;
    return {
      type: RESPONSE_TYPE.RESPONSE,
      data,
    };
  } catch (error) {
    logError(`Error sending message to proxy ${error}`, error as Error);
    return {
      type: RESPONSE_TYPE.ERROR,
      error: {
        message: GENERIC_ERROR_MESSAGE,
        type: RESPONSE_TYPE.ERROR,
        http_code: 500,
      },
    };
  }
};

const normalizeReport = (
  report: Partial<PossibleReportParams>
): PossibleReportParams => {
  return {
    ...NULL_POSSIBLE_REPORT_PARAMS,
    ...report,
    search_type: (report.search_type ||
      '') as PossibleReportParams['search_type'],
  };
};

export const analyzeImageForReports = async (
  imageData: ImageAnalysisRequest,
  logError: (message: string, error: Error) => void,
  token?: string
): Promise<ResponsesAPIResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch('/ms/insight/image-report-identification', {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({ file_base64: imageData.base64Data }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logError(
        `Error analyzing image: ${response.status} ${response.statusText} - ${errorText}`,
        new Error(`HTTP ${response.status}: ${errorText}`)
      );
      return {
        type: RESPONSE_TYPE.ERROR,
        error: {
          message:
            response.status === 429
              ? RATE_LIMIT_EXCEEDED_ERROR_MESSAGE
              : GENERIC_ERROR_MESSAGE,
          type: RESPONSE_TYPE.ERROR,
          http_code: response.status,
        },
      };
    }

    const smartSearchBotResponse = await response.json();

    if (
      smartSearchBotResponse.data?.tool_calls?.length > 0 &&
      smartSearchBotResponse.data.tool_calls[0]?.error &&
      !smartSearchBotResponse.data.tool_calls[0]?.tool
    ) {
      const errorMessage = smartSearchBotResponse.data.tool_calls[0].error;
      const errorResponse: Partial<OpenAI.Responses.Response> = {
        id: smartSearchBotResponse.response_id || `resp_${Date.now()}`,
        object: 'response',
        model: PROXY_MODEL,
        output: [
          {
            id: `msg_${Date.now()}`,
            type: 'message',
            role: 'assistant',
            status: 'completed',
            content: [
              {
                type: 'output_text',
                text: errorMessage,
                annotations: [],
              },
            ],
          },
        ],
      };
      return {
        type: RESPONSE_TYPE.RESPONSE,
        data: errorResponse as OpenAI.Responses.Response,
      };
    }

    if (smartSearchBotResponse.data?.tool_calls?.length > 0) {
      for (const toolCall of smartSearchBotResponse.data.tool_calls) {
        if (
          toolCall.tool === DETECT_IMAGE_REPORTS &&
          toolCall.parameters?.possible_reports
        ) {
          toolCall.parameters.possible_reports =
            toolCall.parameters.possible_reports.map(normalizeReport);
        }
      }
    }

    const responsesAPIFormat: Partial<
      OpenAI.Responses.Response & { context_id?: string }
    > = {
      id: smartSearchBotResponse.response_id || `resp_${Date.now()}`,
      object: 'response',
      model: PROXY_MODEL,
      output: [],
    };

    if (smartSearchBotResponse.data?.tool_calls?.length > 0) {
      const validToolCalls = smartSearchBotResponse.data.tool_calls.filter(
        (toolCall: any) => toolCall.tool && !toolCall.error
      );

      responsesAPIFormat.output = validToolCalls.map((toolCall: any) => ({
        id: toolCall.call_id,
        type: 'function_call',
        call_id: toolCall.call_id,
        name: toolCall.tool,
        arguments: JSON.stringify(toolCall.parameters),
        status: 'completed',
      }));
    } else if (smartSearchBotResponse.data?.answer) {
      responsesAPIFormat.output = [
        {
          id: `msg_${Date.now()}`,
          type: 'message',
          role: 'assistant',
          status: 'completed',
          content: [
            {
              type: 'output_text',
              text: smartSearchBotResponse.data.answer,
              annotations: [],
            },
          ],
        },
      ];
    }

    if (smartSearchBotResponse.context_id) {
      responsesAPIFormat.context_id = smartSearchBotResponse.context_id;
    }

    return {
      type: RESPONSE_TYPE.RESPONSE,
      data: responsesAPIFormat as OpenAI.Responses.Response,
    };
  } catch (error) {
    logError(`Error analyzing image for reports ${error}`, error as Error);
    return {
      type: RESPONSE_TYPE.ERROR,
      error: {
        message: GENERIC_ERROR_MESSAGE,
        type: RESPONSE_TYPE.ERROR,
        http_code: 500,
      },
    };
  }
};
